import axios from "axios";
import { useEffect, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Maintains a live per-product breakdown for an evolving set of scanned RFIDs.
 *
 * - Throttles backend lookups so that even a burst of 200 tags only fires one
 *   request roughly every second (matches the user's "c — once a second"
 *   preference for live updates without melting the network).
 * - Hides raw RFID codes from the operator and surfaces friendly product
 *   names + counts. Unknown tags are reported separately.
 *
 * Returns { breakdown, unknown, refreshing, refresh }.
 *   breakdown : [{ product_id, product_name, count }] sorted by count desc
 *   unknown   : string[] of RFIDs the backend couldn't resolve
 */
export default function useLiveProductBreakdown(rfids, { throttleMs = 1000 } = {}) {
  const [resolved, setResolved] = useState(() => new Map()); // rfid -> {product_id, product_name}
  const [unknown, setUnknown] = useState(() => new Set());
  const [refreshing, setRefreshing] = useState(false);

  const pendingRef = useRef(new Set());      // RFIDs we still need to resolve
  const timerRef = useRef(null);
  const inflightRef = useRef(false);

  // Whenever the input set changes, mark any new RFIDs as pending and (re)schedule.
  useEffect(() => {
    const all = Array.isArray(rfids) ? rfids : Array.from(rfids || []);
    let dirty = false;
    for (const r of all) {
      if (!resolved.has(r) && !unknown.has(r) && !pendingRef.current.has(r)) {
        pendingRef.current.add(r);
        dirty = true;
      }
    }
    if (!dirty) return;
    schedule();
  // intentionally only depend on rfids — schedule/flush deps are stable refs
  // eslint-disable-next-line
  }, [rfids]);

  const schedule = () => {
    if (timerRef.current || inflightRef.current) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      flush();
    }, throttleMs);
  };

  const flush = async () => {
    const batch = Array.from(pendingRef.current);
    if (batch.length === 0) return;
    pendingRef.current.clear();
    inflightRef.current = true;
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/linen/lookup-many`, { rfids: batch });
      const found = (res.data && res.data.items) || [];
      const notFound = (res.data && res.data.unknown) || [];
      setResolved((prev) => {
        const next = new Map(prev);
        for (const f of found) {
          next.set(f.rfid, {
            product_id: f.product_id,
            product_name: f.product_name || "Unknown product",
          });
        }
        return next;
      });
      if (notFound.length) {
        setUnknown((prev) => {
          const next = new Set(prev);
          for (const r of notFound) next.add(r);
          return next;
        });
      }
    } catch {
      // Re-queue this batch so a later schedule can retry.
      for (const r of batch) pendingRef.current.add(r);
    } finally {
      inflightRef.current = false;
      setRefreshing(false);
      // Drain anything that arrived during the request.
      if (pendingRef.current.size > 0) schedule();
    }
  };

  const refresh = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    flush();
  };

  // Build the breakdown directly from the resolved map, only counting RFIDs
  // that are still in the active set (lets `Clear` instantly reset totals).
  const breakdown = (() => {
    const all = new Set(Array.isArray(rfids) ? rfids : Array.from(rfids || []));
    const counts = new Map();
    for (const r of all) {
      const info = resolved.get(r);
      if (!info) continue;
      const key = info.product_id || "unknown";
      const cur = counts.get(key) || { product_id: info.product_id, product_name: info.product_name, count: 0 };
      cur.count += 1;
      counts.set(key, cur);
    }
    return Array.from(counts.values()).sort((a, b) => (a.product_name || "").localeCompare(b.product_name || ""));
  })();

  // Same for "unknown still in the active set".
  const liveUnknown = (() => {
    const all = new Set(Array.isArray(rfids) ? rfids : Array.from(rfids || []));
    return Array.from(unknown).filter((r) => all.has(r));
  })();

  return { breakdown, unknown: liveUnknown, refreshing, refresh };
}
